import React, { useState, useEffect } from 'react';
import { format, parseISO } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { useNavigate, useLocation } from 'react-router-dom';
import FullPageSpinner from '../../FullPageSpinner';
import ReactMarkdown from 'react-markdown';
import { ServerLeaderboardService } from '../../../services/ServerLeaderboardService';
import { useTenant } from '../../../contexts/TenantContext';
import { useAuth } from '../../../contexts/AuthContext';
import { getTenantLocations, getShiftTranscript } from '../../../services/scopedReadsService';
import { computeScore } from '../../../utils/scoreUtils';
import * as Sentry from '@sentry/react';

interface ServerSummary {
  serverId: string;
  serverName: string;
  shiftId: string;
  shiftStarted: string;
  shiftEnded: string | null;
  shiftDuration: number; // in minutes
  totalInteractions: number;
  criteriaStats: {
    upsellPremiumMenu: number;
    mentionSpecificPremiumItem: number;
    describeSpecificPremiumItem: number;
    drinkPromotion: number;
    initialGreetingCount: number;
  }
  premiumStats: {
    regularSales: number;
    premiumSales: number;
    premiumConversionRate: number;
  }
  shifts: ShiftData[];
  bestInteraction?: any;
  worstInteraction?: any;
}

interface Location {
  id: string;
  name: string;
}

interface ShiftData {
  shift_id: string;
  shift_created: string;
  shift_ended: string | null;
  duration_minutes: number;
  interactions_count: number;
  interactions?: Array<{
    id: string;
    data: any;
    start_time: number;
    end_time: number;
    shift_id: string;
    interaction_type: string;
  }>;
}

const ServerSummaryView: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { metricLabels, selectedTenant } = useTenant();
  const { user } = useAuth();
  const queryParams = new URLSearchParams(location.search);

  // Get query parameters
  const urlLocationId = queryParams.get('locationId');
  const urlDate = queryParams.get('date');
  const isAdvancedMode = queryParams.get('advanced') === 'true';

  const [selectedLocation, setSelectedLocation] = useState<string>(urlLocationId || '');
  const [selectedDate, setSelectedDate] = useState<string>(urlDate || format(new Date(), 'yyyy-MM-dd'));
  const [locations, setLocations] = useState<Location[]>([]);
  const [serverSummaries, setServerSummaries] = useState<ServerSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Add state for interactionsByShift
  const [interactionsByShift, setInteractionsByShift] = useState<{ [key: string]: any[] }>({});
  // Add state for copy button
  const [isCopied, setIsCopied] = useState(false);
  // Add state for field loading
  const [isFieldLoading, setIsFieldLoading] = useState(false);

  // State for modals
  const [selectedInteraction, setSelectedInteraction] = useState<{
    text: string;
    reasoning: string;
    serviceCriteria: Array<{ type: string; fulfilled: boolean }>;
    startTime: number;
    endTime: number;
  } | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  // Add expanded state for mobile card view
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());

  // Add state for the shift selection modal
  const [isShiftModalOpen, setIsShiftModalOpen] = useState(false);
  const [selectedServerShifts, setSelectedServerShifts] = useState<ShiftData[]>([]);
  const [selectedServerName, setSelectedServerName] = useState<string>('');
  const [selectedServerId, setSelectedServerId] = useState<string>('');

  // Toggle card expansion state
  const toggleCardExpansion = (shiftId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(shiftId)) {
        newSet.delete(shiftId);
      } else {
        newSet.add(shiftId);
      }
      return newSet;
    });
  };

  // Update URL when filters change
  useEffect(() => {
    if (selectedDate && selectedLocation) {
      const params = new URLSearchParams();
      params.set('date', selectedDate);
      params.set('locationId', selectedLocation);

      // Preserve the advanced parameter if it exists
      if (isAdvancedMode) {
        params.set('advanced', 'true');
      }

      navigate(
        `/restaurant/servers/summary?${params.toString()}`,
        { replace: true }
      );
    }
  }, [selectedDate, selectedLocation, navigate, isAdvancedMode]);

  // Get current date in PST
  const getCurrentDate = () => {
    const pstDate = toZonedTime(new Date(), 'America/Los_Angeles');
    const formattedDate = format(pstDate, 'yyyy-MM-dd');
    console.log('Current date (PST):', formattedDate);
    return formattedDate;
  };

  const CURRENT_DATE = getCurrentDate();

  // Fetch locations
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        // feat-133 Batch-1: scoped ref-lookup replaces the unscoped
        // execute-select `SELECT id,name FROM location`. Tenant is bound
        // server-side from UserContext (Manager/Corporate pinned to own tenant);
        // Admin selects one via the tenant picker. No tenant → no locations.
        const tenantId = selectedTenant || user?.managementUser?.tenant_id || undefined;
        if (!tenantId) {
          setLocations([]);
          return;
        }
        const results = await getTenantLocations(tenantId);
        setLocations(results);

        // Set the first location as default if we don't have a selection yet
        if (results.length > 0 && !selectedLocation) {
          setSelectedLocation(results[0].id);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An unknown error occurred');
        Sentry.captureException(err);
        console.error('Error fetching locations:', err);
      }
    };

    fetchLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTenant, user?.managementUser?.tenant_id]);

  // Fetch server data based on location and selected date
  useEffect(() => {
    const fetchServerData = async () => {
      if (!selectedLocation) return;

      setIsLoading(true);
      setIsFieldLoading(true);
      try {
        console.log('Fetching data for location:', selectedLocation, 'and date:', selectedDate);
        // feat-133 Batch-3: pass tenant from auth context — the aggregation is
        // now tenant-scoped server-side (no client SQL interpolation).
        const tenantId = selectedTenant || user?.managementUser?.tenant_id || undefined;
        const stats = await ServerLeaderboardService.getServerLeaderboardData(
          selectedDate,
          selectedDate,
          undefined,
          false,
          selectedLocation,
          false,
          tenantId
        );

        // Transform the stats into ServerSummary format
        const summaries = stats.map(stat => {
          // Find best and worst interactions across all shifts
          let bestInteraction = null;
          let worstInteraction = null;
          let maxFulfillment = -1;
          let minFulfillment = Infinity;

          // Debug: count how many interactions of each type we have
          const interactionTypeCounts: Record<string, number> = {};
          stat.shifts.forEach(shift => {
            shift.interactions?.forEach(interaction => {
              const type = interaction.interaction_type || 'unknown';
              interactionTypeCounts[type] = (interactionTypeCounts[type] || 0) + 1;
            });
          });
          console.log(`Server ${stat.server_name} interaction types:`, interactionTypeCounts);

          stat.shifts.forEach(shift => {
            shift.interactions?.forEach(interaction => {
              // The tenant's prompt config decides which interaction_types
              // carry service_criteria. Trust the data, not a hardcoded
              // SAJJ-flavored whitelist.
              if (interaction?.data?.service_criteria) {
                const fulfillmentCount = interaction.data.service_criteria.filter(
                  (c: any) => c.fulfilled === true
                ).length;

                if (fulfillmentCount > maxFulfillment) {
                  maxFulfillment = fulfillmentCount;
                  // Make sure shift_id is assigned to the interaction
                  bestInteraction = {
                    ...interaction,
                    shift_id: shift.shift_id
                  };
                }
                if (fulfillmentCount < minFulfillment && fulfillmentCount > 0) { // Ensure it has at least one fulfilled criteria
                  minFulfillment = fulfillmentCount;
                  // Make sure shift_id is assigned to the interaction
                  worstInteraction = {
                    ...interaction,
                    shift_id: shift.shift_id
                  };
                }
              }
            });
          });

          return {
            serverId: stat.server_id,
            serverName: stat.server_name,
            shiftId: stat.shifts[0]?.shift_id || '',
            shiftStarted: stat.shifts[0]?.shift_created || '',
            shiftEnded: stat.shifts[0]?.shift_ended || null,
            shiftDuration: stat.shifts[0]?.duration_minutes || 0,
            totalInteractions: stat.total_interactions,
            criteriaStats: {
              upsellPremiumMenu: stat.criteria_stats.upsell_count,
              mentionSpecificPremiumItem: stat.criteria_stats.mention_count,
              describeSpecificPremiumItem: stat.criteria_stats.describe_count,
              drinkPromotion: stat.criteria_stats.drink_promotion_count,
              initialGreetingCount: stat.initial_greeting_count
            },
            premiumStats: {
              regularSales: stat.premium_stats?.regular_sales || 0,
              premiumSales: stat.premium_stats?.premium_sales || 0,
              premiumConversionRate: stat.premium_stats?.premium_conversion_rate || 0
            },
            shifts: stat.shifts,
            bestInteraction,
            worstInteraction
          };
        });

        // Sort server summaries by score (highest first)
        summaries.sort((a, b) => calculateServerScore(b) - calculateServerScore(a));

        setServerSummaries(summaries);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An unknown error occurred');
        Sentry.captureException(err);
        console.error('Error fetching server data:', err);
      } finally {
        setIsLoading(false);
        setIsFieldLoading(false);
      }
    };

    fetchServerData();
  }, [selectedLocation, selectedDate]);

  // Format date for display
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';

    // Ensure the date string is treated as UTC
    const utcDateString = dateString.endsWith('Z') ? dateString : `${dateString}Z`;
    const date = new Date(utcDateString);

    return format(date, 'h:mm a');
  };

  // Format date with day for display
  const formatDateWithDay = (dateString: string | null) => {
    if (!dateString) return 'N/A';

    // Ensure the date string is treated as UTC
    const utcDateString = dateString.endsWith('Z') ? dateString : `${dateString}Z`;
    const date = new Date(utcDateString);

    return format(date, 'MMM d, yyyy h:mm a');
  };

  // Format date for title
  const formatDateForTitle = (dateString: string) => {
    try {
      // Use the date string directly instead of creating a new Date object
      // This prevents timezone conversion issues when formatting
      const [year, month, day] = dateString.split('-').map(Number);

      // Create a formatted date without timezone conversion
      const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ];

      return `${months[month - 1]} ${day}, ${year}`;
    } catch (e) {
      return dateString;
    }
  };

  // Get location name from ID
  const getLocationName = (locationId: string): string => {
    const location = locations.find(loc => loc.id === locationId);
    return location ? location.name : 'Unknown Location';
  };

  // Format percentage
  const formatPercentage = (value: number, total: number) => {
    if (total === 0) return '0%';
    return `${Math.round((value / total) * 100)}%`;
  };

  // Remove the "Server Feedback Session" header from markdown if present
  const preprocessSummaryText = (text: string | null): string => {
    if (!text) return '';

    // Remove "# Server Feedback Session" (with any number of # characters)
    return text.replace(/^#+\s*Server\s+Feedback\s+Session\s*$/m, '').trim();
  };

  // Format duration in hours and minutes
  const formatDuration = (minutes: number): string => {
    if (minutes <= 0) return 'N/A';

    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;

    return `${hours > 0 ? `${hours}h ` : ''}${mins}m`;
  };

  // Modify the handler for row clicks to show the shift selection modal
  const handleRowClick = (serverId: string, serverShifts: ShiftData[] | undefined, serverName: string) => {
    if (isAdvancedMode && serverShifts && serverShifts.length > 0) {
      // Open the shift selection modal instead of navigating directly
      setSelectedServerShifts(serverShifts);
      setSelectedServerName(serverName);
      setSelectedServerId(serverId);
      setIsShiftModalOpen(true);
    }
  };

  // Function to handle shift selection
  const handleShiftSelect = (shiftId: string) => {
    // Navigate to the selected shift
    navigate(`/restaurant/shifts/${shiftId}/transcript/${selectedServerId}`);
    setIsShiftModalOpen(false);
  };

  // Function to close the shift modal
  const closeShiftModal = () => {
    setIsShiftModalOpen(false);
    setSelectedServerShifts([]);
    setSelectedServerName('');
    setSelectedServerId('');
  };

  // Function to extract a snippet from text
  const extractSnippet = (text: string | undefined | null, maxLength: number = 120): string => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + "...";
  };

  // Update the interaction modal to use the new interaction structure
  // feat-218: `showFullQuoteOnly` is REQUIRED — deliberately no default.
  //
  // It used to default to `false`, which defaulted callers into the `else` branch at :456-457 that
  // reads `interaction.data.text` and `.data.reasoning`. feat-218 (D7) stops the backend sending
  // `reasoning` on the server-stats/criteria response, and no writer has ever emitted `text`, so
  // that branch now renders empty strings.
  //
  // All four existing call sites (:826, :847, :1040, :1061) pass `true` explicitly, so making this
  // required is a NO-OP today. The point is tomorrow: a fifth call written the natural way —
  // `openInteractionDetails(x)` — would previously have taken the dead branch and silently rendered
  // nothing. Now it will not compile.
  //
  // D7's entire justification (dropping `reasoning`, ~40.7% of blob bytes) rests on that branch
  // being unreachable. Without this, that premise is true only by coincidence.
  const openInteractionDetails = async (interaction: any, showFullQuoteOnly: boolean) => {
    console.log('Opening interaction details (full):', JSON.stringify(interaction, null, 2));
    if (!interaction?.data) {
      console.log('No interaction data available');
      return;
    }

    try {
      let text = '';
      let reasoning = '';
      let serviceCriteria: any[] = [];

      if (showFullQuoteOnly) {
        // Check if shift_id is present at the top level
        const interactionShiftId = interaction.shift_id;

        console.log('Fetching full transcript for interaction:', {
          interactionObject: interaction,
          interactionType: interaction.interaction_type,
          shiftId: interactionShiftId,
          startTime: interaction.start_time,
          endTime: interaction.end_time
        });

        // If we have shift_id and timing info, fetch the actual transcript text
        if (interactionShiftId && typeof interaction.start_time === 'number' && typeof interaction.end_time === 'number') {
          try {
            const shiftId = interactionShiftId;
            const startTime = interaction.start_time;
            const endTime = interaction.end_time;

            // feat-133 Batch-2: scoped transcript-window replaces the execute-select
            // `SELECT text FROM transcript_part WHERE shift_id=.. AND start_time
            // BETWEEN start AND end`. The shift is authorized server-side (404 on a
            // foreign shift, closing the PII leak); the window is %s-bound inclusive.
            // Same rows → identical joined text.
            const parts = await getShiftTranscript(shiftId, { start: startTime, end: endTime });

            // Combine all transcript parts into a single text
            text = parts.map((part) => part.text).join(' ');

            // Format the text to be more readable
            // Split long sentences and add paragraph breaks
            text = formatTranscriptText(text);
          } catch (error) {
            console.error('Error fetching transcript text:', error);
            Sentry.captureException(error);
            text = 'Error loading full transcript';
          }
        } else {
          console.log('Missing required data for transcript fetch:', {
            hasShiftId: !!interactionShiftId,
            shiftIdValue: interactionShiftId,
            hasStartTime: typeof interaction.start_time === 'number',
            startTimeValue: interaction.start_time,
            hasEndTime: typeof interaction.end_time === 'number',
            endTimeValue: interaction.end_time
          });
          text = 'No transcript available';
        }
      } else {
        text = interaction.data.text || '';
        reasoning = interaction.data.reasoning || '';
        serviceCriteria = interaction.data.service_criteria || [];
      }

      console.log('Setting interaction with text:', text);
      setSelectedInteraction({
        text,
        reasoning,
        serviceCriteria,
        startTime: interaction.start_time,
        endTime: interaction.end_time
      });
      setIsModalOpen(true);
    } catch (error) {
      console.error('Error processing interaction data:', error);
      Sentry.captureException(error);
    }
  };

  // Close the modal
  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedInteraction(null);
  };

  // Helper function to format service criteria type for display (supporting both old and new formats)
  const formatCriteriaType = (type: string): string => {
    switch (type) {
      // Legacy SAJJ aliases (pre-prompt-config). Kept for old shift data.
      case 'upsell_premium_menu':
        return 'Upsell Premium Menu';
      case 'mention_specific_premium_item':
        return 'Mention Specific Premium Item';
      case 'describe_specific_premium_item':
        return 'Describe Specific Premium Item';
      default:
        // Look up the tenant's display label (whatever metric_key it is —
        // metric_1, entree_target, add_on_upsell, etc.). Falls through to a
        // title-cased version of the key when no label is configured.
        if (metricLabels[type]) {
          return `${metricLabels[type]} Upsell`;
        }
        return type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    }
  };

  // Helper function to format service criteria as emojis, similar to transcript_annotator
  const formatCriteriaEmojis = (criteria: {
    upsellPremiumMenu: number,
    mentionSpecificPremiumItem: number,
    describeSpecificPremiumItem: number
  }, total: number): string => {
    if (total === 0) return "";

    const criteriaMap = {
      upsellPremiumMenu: "💰",
      mentionSpecificPremiumItem: "🥩",
      describeSpecificPremiumItem: "🗣️"
    };

    // Format emoji with checkmark for fulfilled, dash for not fulfilled
    const feedbackParts = Object.entries(criteriaMap).map(([key, emoji]) => {
      const typedKey = key as keyof typeof criteria;
      // Simple binary check - if criteria is used at all, it's a checkmark, otherwise dash
      return `${emoji}${criteria[typedKey] > 0 ? "✅" : "➖"}`;
    });

    return feedbackParts.join("  ");
  };

  // Helper function to format criteria emojis for a specific interaction (supporting both formats)
  const formatInteractionCriteriaEmojis = (serviceCriteria: Array<{ type: string, fulfilled: boolean }> | undefined): string => {
    if (!serviceCriteria || serviceCriteria.length === 0) return "";

    // Support both old and new formats
    const oldCriteriaMap = {
      upsell_premium_menu: "💰",
      mention_specific_premium_item: "🥩",
      describe_specific_premium_item: "🗣️"
    };

    const newCriteriaMap: Record<string, string> = {
      metric_1: "\uD83E\uDD69",
      metric_2: "\uD83C\uDF56",
      metric_3: "\uD83C\uDF6B",
    };

    // Create a lookup map for criteria by type
    const criteriaLookup = serviceCriteria.reduce((acc, criterion) => {
      acc[criterion.type] = criterion.fulfilled;
      return acc;
    }, {} as Record<string, boolean>);

    // Determine which format to use
    const hasNewFormat = Object.keys(newCriteriaMap).some(type => criteriaLookup[type] !== undefined);
    const criteriaMap = hasNewFormat ? newCriteriaMap : oldCriteriaMap;

    const feedbackParts = Object.entries(criteriaMap).map(([criterionType, emoji]) => {
      // Check if this criterion exists and is fulfilled
      return `${emoji}${criteriaLookup[criterionType] ? "✅" : "➖"}`;
    });

    return feedbackParts.join("  ");
  };

  // Get a random server hint based on the shift ID (consistent for same shift)
  const getServerHint = (shiftId: string): string => {
    // Server hints array
    const serverHints = [
      "Learn the complete menu inside and out, especially premium cuts and their unique qualities.",
      "Begin with a brief explanation of the Korean BBQ experience for first-time guests.",
      "Highlight signature or seasonal drink menu items immediately after greeting.",
      "When guests are deciding, suggest: \"Many of our regular guests start with the Premium ribeye® and Premium wagyu® to compare the flavor profiles.\"",
      "For hesitant guests, offer: \"Would you like to add just one Premium wagyu ala carte to your order to experience the difference?\"",
      "Create urgency with phrases like: \"Our Premium marbling center cut marinated short rib is limited each day and often sells out.\"",
      "For large groups, suggest premium items that can be shared for a more economical way to experience luxury cuts.",
      "Use descriptive language when describing premium items: \"The Carne asada has a perfect char on the outside while remaining juicy inside.\"",
      "For groups celebrating special occasions, suggest premium platters as \"celebration-worthy\" selections.",
      "Our Premium marinated long bone short rib steak® is limited each day - we've only got a few portions left tonight.",
      "Based on what you've ordered, I think you'd really enjoy our Premium wagyu® - it has similar flavors but more intense marbling.",
      "Kimchi fried rice® pairs perfectly with our Premium wagyu® - they're designed to complement each other.",
      "For large groups mention that an ala carte premium item is only a few dollars each.",
      "Our Premium long bone short rib steak® makes for an amazing photo opportunity - it's our most Instagrammed dish.",
      "Our Cajun shrimp® is an exclusive premium item you won't find at other Korean BBQ restaurants.",
      "Before I take your food order, let me tell you about our seasonal cocktails that perfectly complement our premium meats.",
      "Before we start with food, have you tried our new craft cocktails? They've just been released this week and go great with our Premium wagyu®"
    ];

    // Use a simple hash function to get a consistent index based on shift ID
    let hash = 0;
    for (let i = 0; i < shiftId.length; i++) {
      hash = ((hash << 5) - hash) + shiftId.charCodeAt(i);
      hash = hash & hash; // Convert to 32bit integer
    }

    // Make sure it's positive and within bounds of the array
    const index = Math.abs(hash) % serverHints.length;
    return serverHints[index];
  };

  // Calculate server score as average of all 4 metrics
  const calculateServerScore = (server: ServerSummary): number => {
    if (server.criteriaStats.initialGreetingCount === 0) return 0;

    const upsellPercent = Math.round((server.criteriaStats.upsellPremiumMenu / server.criteriaStats.initialGreetingCount) * 100);
    const mentionPercent = Math.round((server.criteriaStats.mentionSpecificPremiumItem / server.criteriaStats.initialGreetingCount) * 100);
    const describePercent = Math.round((server.criteriaStats.describeSpecificPremiumItem / server.criteriaStats.initialGreetingCount) * 100);
    const drinkPercent = Math.round((server.criteriaStats.drinkPromotion / server.criteriaStats.initialGreetingCount) * 100);

    return computeScore([upsellPercent, mentionPercent, describePercent], drinkPercent);
  };

  // Format transcript text to be more readable
  const formatTranscriptText = (text: string): string => {
    if (!text) return '';

    // Split into sentences and create paragraphs
    let formattedText = text
      // Replace periods followed by spaces with period and newline
      .replace(/\.\s+/g, '.\n')
      // Replace question marks followed by spaces with question mark and newline
      .replace(/\?\s+/g, '?\n')
      // Replace exclamation marks followed by spaces with exclamation mark and newline
      .replace(/!\s+/g, '!\n');

    // Add extra spacing between clear paragraphs (consecutive newlines)
    formattedText = formattedText.replace(/\n\n/g, '\n\n');

    return formattedText;
  };

  if (isLoading && !serverSummaries.length) {
    return <FullPageSpinner />;
  }

  if (error && !serverSummaries.length) {
    return <div className="text-red-500 p-4">Error: {error}</div>;
  }

  return (
    <div className="container mx-auto px-4 py-6 max-w-7xl">
      <h1 className="text-2xl md:text-3xl font-bold mb-4 md:mb-6">
        {selectedDate && selectedLocation ? (
          `${formatDateForTitle(selectedDate)} ${getLocationName(selectedLocation)} Server Performance Summary`
        ) : (
          "Server Performance Summary"
        )}
      </h1>

      <div className="bg-white shadow-md rounded-lg p-4 md:p-6 mb-6">
        {/* Only show the filters in advanced mode */}
        {isAdvancedMode && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label htmlFor="date-select" className="block text-sm font-medium text-gray-700 mb-1">
                Select Date
              </label>
              <input
                id="date-select"
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                disabled={isFieldLoading}
              />
            </div>
            <div>
              <label htmlFor="location-select" className="block text-sm font-medium text-gray-700 mb-1">
                Select Location
              </label>
              <select
                id="location-select"
                value={selectedLocation}
                onChange={(e) => setSelectedLocation(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                disabled={isFieldLoading}
              >
                <option value="">Select a location</option>
                {locations.map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {isFieldLoading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <span className="ml-3 text-gray-500">Loading data...</span>
          </div>
        ) : serverSummaries.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500">No shifts found for the selected date and location.</p>
          </div>
        ) : (
          <>
            {/* Desktop view - Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[15%]">
                      Server
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[15%]">
                      Key Metrics
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[70%]">
                      Summary
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {serverSummaries.map((summary) => (
                    <tr
                      key={summary.serverId}
                      className={isAdvancedMode ? "hover:bg-blue-50 cursor-pointer transition-colors duration-150" : ""}
                      onClick={() => isAdvancedMode && handleRowClick(summary.serverId, summary.shifts, summary.serverName)}
                      title={isAdvancedMode ? "Click to view shifts" : ""}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`text-md font-medium ${isAdvancedMode ? "text-blue-600" : "text-gray-900"}`}>
                          {summary.serverName}
                          {isAdvancedMode && (
                            <span className="ml-1 text-xs text-blue-500">→</span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500">{formatDateWithDay(summary.shiftStarted)}</div>
                        {summary.shiftDuration > 0 && (
                          <div className="text-xs text-gray-500">Duration: {formatDuration(summary.shiftDuration)}</div>
                        )}
                        <div className="flex items-center space-x-2">
                          <span className="text-xs text-gray-500">Greetings:</span>
                          <span className="text-xs font-medium">{summary.criteriaStats.initialGreetingCount}</span>
                        </div>
                        <div className="mt-1 text-sm font-semibold text-blue-600">
                          Score: {calculateServerScore(summary)}%
                        </div>
                        {summary.premiumStats.regularSales > 0 && (
                          <div className="mt-1 text-xs text-gray-700">
                            <span className="font-medium">Premium Sales:</span>{' '}
                            <span>{summary.premiumStats.premiumSales}/{summary.premiumStats.regularSales}</span>{' '}
                            <span className="text-purple-700 font-medium">({Math.round(summary.premiumStats.premiumConversionRate)}%)</span>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-3">
                          {/* Compact metrics with mini progress bars */}
                          <div>
                            <div className="flex justify-between items-center mb-1">
                              <span className="text-xs font-medium">Premium Menu</span>
                              <span className="text-xs font-medium">
                                {formatPercentage(summary.criteriaStats.upsellPremiumMenu, summary.criteriaStats.initialGreetingCount)}
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-1.5">
                              <div
                                className="bg-blue-600 h-1.5 rounded-full"
                                style={{ width: `${summary.criteriaStats.initialGreetingCount > 0 ? (summary.criteriaStats.upsellPremiumMenu / summary.criteriaStats.initialGreetingCount * 100) : 0}%` }}
                              ></div>
                            </div>
                          </div>

                          <div>
                            <div className="flex justify-between items-center mb-1">
                              <span className="text-xs font-medium">Item Mention</span>
                              <span className="text-xs font-medium">
                                {formatPercentage(summary.criteriaStats.mentionSpecificPremiumItem, summary.criteriaStats.initialGreetingCount)}
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-1.5">
                              <div
                                className="bg-green-500 h-1.5 rounded-full"
                                style={{ width: `${summary.criteriaStats.initialGreetingCount > 0 ? (summary.criteriaStats.mentionSpecificPremiumItem / summary.criteriaStats.initialGreetingCount * 100) : 0}%` }}
                              ></div>
                            </div>
                          </div>

                          <div>
                            <div className="flex justify-between items-center mb-1">
                              <span className="text-xs font-medium">Item Description</span>
                              <span className="text-xs font-medium">
                                {formatPercentage(summary.criteriaStats.describeSpecificPremiumItem, summary.criteriaStats.initialGreetingCount)}
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-1.5">
                              <div
                                className="bg-purple-500 h-1.5 rounded-full"
                                style={{ width: `${summary.criteriaStats.initialGreetingCount > 0 ? (summary.criteriaStats.describeSpecificPremiumItem / summary.criteriaStats.initialGreetingCount * 100) : 0}%` }}
                              ></div>
                            </div>
                          </div>

                          <div>
                            <div className="flex justify-between items-center mb-1">
                              <span className="text-xs font-medium">Drink Promotions</span>
                              <span className="text-xs font-medium">
                                {formatPercentage(summary.criteriaStats.drinkPromotion, summary.criteriaStats.initialGreetingCount)}
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-1.5">
                              <div
                                className="bg-yellow-500 h-1.5 rounded-full"
                                style={{ width: `${summary.criteriaStats.initialGreetingCount > 0 ? (summary.criteriaStats.drinkPromotion / summary.criteriaStats.initialGreetingCount * 100) : 0}%` }}
                              ></div>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        {summary.bestInteraction && summary.worstInteraction ? (
                          <div className="space-y-4">
                            {/* Good Interaction - Compact view */}
                            <div className="border-l-4 border-green-500 pl-3 py-2">
                              <h5 className="font-medium text-green-700 mb-2 text-sm">Best Interaction Example</h5>
                              {/* Criteria emojis for this good interaction */}
                              <div className="text-lg mb-2">
                                {formatInteractionCriteriaEmojis(summary.bestInteraction?.data?.service_criteria)}
                              </div>
                              <p className="text-sm text-gray-700 mb-2">
                                {summary.bestInteraction?.data?.text ? extractSnippet(summary.bestInteraction.data.text) : ''}
                              </p>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openInteractionDetails(summary.bestInteraction, true);
                                }}
                                className="text-xs bg-green-100 hover:bg-green-200 text-green-800 font-medium py-1 px-2 rounded transition"
                              >
                                Full Quote
                              </button>
                            </div>

                            {/* Bad Interaction - Compact view */}
                            <div className="border-l-4 border-red-500 pl-3 py-2">
                              <h5 className="font-medium text-red-700 mb-2 text-sm">Improvement Opportunity</h5>
                              {/* Criteria emojis for this bad interaction */}
                              <div className="text-lg mb-2">
                                {formatInteractionCriteriaEmojis(summary.worstInteraction?.data?.service_criteria)}
                              </div>
                              <p className="text-sm text-gray-700 mb-2">
                                {summary.worstInteraction?.data?.text ? extractSnippet(summary.worstInteraction.data.text) : ''}
                              </p>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openInteractionDetails(summary.worstInteraction, true);
                                }}
                                className="text-xs bg-red-100 hover:bg-red-200 text-red-800 font-medium py-1 px-2 rounded transition"
                              >
                                Full Quote
                              </button>
                            </div>

                            {/* Server Hint/Tip for desktop view */}
                            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3">
                              <h5 className="text-sm font-medium text-yellow-800 mb-1">Tip for improvement:</h5>
                              <p className="text-sm text-yellow-700">{getServerHint(summary.shiftId)}</p>
                            </div>

                            {!summary.bestInteraction && !summary.worstInteraction && (
                              <div className="text-gray-500 italic">No interaction examples available</div>
                            )}
                          </div>
                        ) : (
                          <div>
                            <span className="text-gray-400">No interaction examples available</span>

                            {/* Server Hint/Tip for desktop view with no interaction examples */}
                            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 mt-4">
                              <h5 className="text-sm font-medium text-yellow-800 mb-1">Tip for improvement:</h5>
                              <p className="text-sm text-yellow-700">{getServerHint(summary.shiftId)}</p>
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile view - Cards */}
            <div className="md:hidden space-y-6">
              {serverSummaries.map((summary) => (
                <div
                  key={summary.serverId}
                  className={`bg-white border rounded-lg shadow-sm overflow-hidden ${isAdvancedMode ? "active:bg-blue-50" : ""}`}
                  onClick={() => isAdvancedMode && handleRowClick(summary.serverId, summary.shifts, summary.serverName)}
                >
                  {/* Server Info Header */}
                  <div className="p-4 border-b bg-gray-50">
                    <div className="flex justify-between items-center">
                      <div>
                        <h3 className={`text-lg font-medium ${isAdvancedMode ? "text-blue-600" : "text-gray-900"}`}>
                          {summary.serverName}
                          {isAdvancedMode && <span className="ml-1 text-xs text-blue-500">→</span>}
                        </h3>
                        <p className="text-xs text-gray-500">{formatDateWithDay(summary.shiftStarted)}</p>
                        <div className="flex items-center space-x-3 mt-1">
                          <span className="text-xs text-gray-500">
                            {summary.shiftDuration > 0 ? `Duration: ${formatDuration(summary.shiftDuration)}` : ''}
                          </span>
                          <div className="flex items-center space-x-2">
                            <span className="text-xs text-gray-500">Greetings:</span>
                            <span className="text-xs font-medium">{summary.criteriaStats.initialGreetingCount}</span>
                          </div>
                        </div>
                        <div className="mt-1 text-sm font-semibold text-blue-600">
                          Score: {calculateServerScore(summary)}%
                        </div>
                        {summary.premiumStats.regularSales > 0 && (
                          <div className="mt-1 text-xs text-gray-700 flex flex-wrap items-center gap-2">
                            <span className="font-medium">Premium Sales:</span>
                            <span>{summary.premiumStats.premiumSales}/{summary.premiumStats.regularSales}</span>
                            <span className="text-purple-700 font-medium">({Math.round(summary.premiumStats.premiumConversionRate)}%)</span>
                          </div>
                        )}
                      </div>
                      <button
                        onClick={(e) => toggleCardExpansion(summary.serverId, e)}
                        className="text-gray-500 hover:text-gray-700"
                        aria-label={expandedCards.has(summary.serverId) ? "Collapse details" : "Expand details"}
                      >
                        <svg
                          className={`w-6 h-6 transition-transform ${expandedCards.has(summary.serverId) ? 'transform rotate-180' : ''}`}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* Metrics Section */}
                  <div className="p-4 border-b">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Performance Metrics</h4>
                    <div className="space-y-3">
                      <div>
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-xs font-medium">Premium Menu</span>
                          <span className="text-xs font-medium">
                            {formatPercentage(summary.criteriaStats.upsellPremiumMenu, summary.criteriaStats.initialGreetingCount)}
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full"
                            style={{ width: `${summary.criteriaStats.initialGreetingCount > 0 ? (summary.criteriaStats.upsellPremiumMenu / summary.criteriaStats.initialGreetingCount * 100) : 0}%` }}
                          ></div>
                        </div>
                      </div>

                      {/* Add premium sales metrics */}
                      {summary.premiumStats.regularSales > 0 && (
                        <div>
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-xs font-medium">Premium Conversion</span>
                            <span className="text-xs font-medium">
                              {summary.premiumStats.premiumSales}/{summary.premiumStats.regularSales} ({Math.round(summary.premiumStats.premiumConversionRate)}%)
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-purple-600 h-2 rounded-full"
                              style={{ width: `${summary.premiumStats.premiumConversionRate}%` }}
                            ></div>
                          </div>
                        </div>
                      )}

                      <div>
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-xs font-medium">Item Mention</span>
                          <span className="text-xs font-medium">
                            {formatPercentage(summary.criteriaStats.mentionSpecificPremiumItem, summary.criteriaStats.initialGreetingCount)}
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-green-500 h-2 rounded-full"
                            style={{ width: `${summary.criteriaStats.initialGreetingCount > 0 ? (summary.criteriaStats.mentionSpecificPremiumItem / summary.criteriaStats.initialGreetingCount * 100) : 0}%` }}
                          ></div>
                        </div>
                      </div>

                      <div>
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-xs font-medium">Item Description</span>
                          <span className="text-xs font-medium">
                            {formatPercentage(summary.criteriaStats.describeSpecificPremiumItem, summary.criteriaStats.initialGreetingCount)}
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-purple-500 h-2 rounded-full"
                            style={{ width: `${summary.criteriaStats.initialGreetingCount > 0 ? (summary.criteriaStats.describeSpecificPremiumItem / summary.criteriaStats.initialGreetingCount * 100) : 0}%` }}
                          ></div>
                        </div>
                      </div>

                      <div>
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-xs font-medium">Drink Promotions</span>
                          <span className="text-xs font-medium">
                            {formatPercentage(summary.criteriaStats.drinkPromotion, summary.criteriaStats.initialGreetingCount)}
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-yellow-500 h-2 rounded-full"
                            style={{ width: `${summary.criteriaStats.initialGreetingCount > 0 ? (summary.criteriaStats.drinkPromotion / summary.criteriaStats.initialGreetingCount * 100) : 0}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Summary Section - Expandable on mobile */}
                  <div className={`${expandedCards.has(summary.serverId) ? 'block' : 'hidden'} p-4`}>
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Performance Summary</h4>

                    {summary.bestInteraction && summary.worstInteraction ? (
                      <div className="space-y-4">
                        {/* Good Interaction - Compact view */}
                        <div className="border-l-4 border-green-500 pl-3 py-2">
                          <h5 className="font-medium text-green-700 mb-2 text-sm">Best Interaction Example</h5>
                          {/* Criteria emojis for this good interaction */}
                          <div className="text-lg mb-2">
                            {formatInteractionCriteriaEmojis(summary.bestInteraction?.data?.service_criteria)}
                          </div>
                          <p className="text-sm text-gray-700 mb-2">
                            {summary.bestInteraction?.data?.text ? extractSnippet(summary.bestInteraction.data.text) : ''}
                          </p>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openInteractionDetails(summary.bestInteraction, true);
                            }}
                            className="text-xs bg-green-100 hover:bg-green-200 text-green-800 font-medium py-1 px-2 rounded transition"
                          >
                            Full Quote
                          </button>
                        </div>

                        {/* Bad Interaction - Compact view */}
                        <div className="border-l-4 border-red-500 pl-3 py-2">
                          <h5 className="font-medium text-red-700 mb-2 text-sm">Improvement Opportunity</h5>
                          {/* Criteria emojis for this bad interaction */}
                          <div className="text-lg mb-2">
                            {formatInteractionCriteriaEmojis(summary.worstInteraction?.data?.service_criteria)}
                          </div>
                          <p className="text-sm text-gray-700 mb-2">
                            {summary.worstInteraction?.data?.text ? extractSnippet(summary.worstInteraction.data.text) : ''}
                          </p>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openInteractionDetails(summary.worstInteraction, true);
                            }}
                            className="text-xs bg-red-100 hover:bg-red-200 text-red-800 font-medium py-1 px-2 rounded transition"
                          >
                            Full Quote
                          </button>
                        </div>

                        {/* Server Hint/Tip for desktop view */}
                        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3">
                          <h5 className="text-sm font-medium text-yellow-800 mb-1">Tip for improvement:</h5>
                          <p className="text-sm text-yellow-700">{getServerHint(summary.shiftId)}</p>
                        </div>

                        {!summary.bestInteraction && !summary.worstInteraction && (
                          <div className="text-gray-500 italic">No interaction examples available</div>
                        )}
                      </div>
                    ) : (
                      <div>
                        <span className="text-gray-400">No interaction examples available</span>

                        {/* Server Hint/Tip for desktop view with no interaction examples */}
                        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 mt-4">
                          <h5 className="text-sm font-medium text-yellow-800 mb-1">Tip for improvement:</h5>
                          <p className="text-sm text-yellow-700">{getServerHint(summary.shiftId)}</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* "View Summary" button when card is collapsed */}
                  {!expandedCards.has(summary.serverId) && (
                    <div className="p-3 border-t bg-gray-50 text-center">
                      <button
                        onClick={(e) => toggleCardExpansion(summary.serverId, e)}
                        className="text-sm text-blue-600 font-medium hover:text-blue-800"
                      >
                        View Performance Summary
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Text Summary Section - Only show in advanced mode and when not loading */}
      {isAdvancedMode && !isFieldLoading && (
        <div className="bg-white shadow-md rounded-lg p-4 md:p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">Text Summary</h2>
            <button
              onClick={() => {
                const summary = document.getElementById('text-summary-content');
                if (summary) {
                  navigator.clipboard.writeText(summary.innerText);
                  setIsCopied(true);
                  // Reset after 2 seconds
                  setTimeout(() => {
                    setIsCopied(false);
                  }, 2000);
                }
              }}
              className={`${isCopied ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'} text-white px-4 py-2 rounded-md text-sm font-medium transition-colors`}
              disabled={isCopied}
            >
              {isCopied ? 'Copied!' : 'Copy to Clipboard'}
            </button>
          </div>

          <div id="text-summary-content" className="bg-gray-50 p-4 rounded-md font-mono text-sm whitespace-pre-wrap">
            {(() => {
              // Group servers by compliance (1+ hour vs less than 1 hour)
              const compliantServers = serverSummaries
                .filter(server => server.shiftDuration >= 60) // 60 minutes = 1 hour
                .map(server => {
                  return {
                    ...server,
                    averageScore: calculateServerScore(server)
                  };
                })
                .sort((a, b) => b.averageScore - a.averageScore); // Sort by averageScore descending

              const nonCompliantServers = serverSummaries
                .filter(server => server.shiftDuration < 60)
                .sort((a, b) => b.shiftDuration - a.shiftDuration); // Sort by duration descending

              // Build the summary text
              let summaryText = `${formatDateForTitle(selectedDate)} - ${getLocationName(selectedLocation)}\n\n`;

              if (compliantServers.length > 0) {
                summaryText += "Server Performance:\n";
                compliantServers.forEach((server, index) => {
                  summaryText += `${index + 1}. ${server.serverName}: ${Math.round(server.averageScore)}%\n`;
                });
                summaryText += "\n";
              } else {
                summaryText += "No servers with sufficient data (1+ hour).\n\n";
              }

              // Only include non-compliant section if there are any
              if (nonCompliantServers.length > 0) {
                summaryText += "Non-compliant servers (less than 1 hour):\n";
                nonCompliantServers.forEach((server, index) => {
                  summaryText += `${index + 1}. ${server.serverName}: ${formatDuration(server.shiftDuration)}\n`;
                });
                summaryText += "\n";
              }

              // Add link to view details with shorter label
              summaryText += `Details: https://bespokecast.ai/restaurant/servers/summary?date=${selectedDate}&locationId=${selectedLocation}`;

              return summaryText;
            })()}
          </div>
        </div>
      )}

      {/* Modal for showing interaction details */}
      {isModalOpen && selectedInteraction && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50 p-4" onClick={closeModal}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-4 md:p-6">
              <div className="flex justify-between items-start">
                <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-4">Full Quote</h3>
                <button onClick={closeModal} className="text-gray-400 hover:text-gray-500">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="mb-6">
                <div className="bg-gray-50 p-4 rounded-md text-sm whitespace-pre-line leading-relaxed">
                  {selectedInteraction.text}
                </div>
              </div>

              {selectedInteraction.serviceCriteria && selectedInteraction.serviceCriteria.length > 0 && (
                <div className="mb-6">
                  <h4 className="font-medium text-gray-700 mb-2">Service Criteria Results</h4>
                  <div className="space-y-2">
                    {selectedInteraction.serviceCriteria.map((criteria, index) => (
                      <div key={index} className="flex items-center">
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center mr-3 ${criteria.fulfilled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {criteria.fulfilled ? (
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          ) : (
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                          )}
                        </div>
                        <span className={`${criteria.fulfilled ? 'text-green-800' : 'text-red-800'}`}>
                          {formatCriteriaType(criteria.type)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedInteraction.reasoning && (
                <div>
                  <h4 className="font-medium text-gray-700 mb-2">Analysis</h4>
                  <div className="bg-gray-50 p-4 rounded-md text-sm">
                    {selectedInteraction.reasoning}
                  </div>
                </div>
              )}
            </div>

            <div className="bg-gray-50 px-4 md:px-6 py-3 flex justify-end">
              <button
                onClick={closeModal}
                className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Modal for selecting shifts */}
      {isShiftModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50 p-4" onClick={closeShiftModal}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-4 md:p-6">
              <div className="flex justify-between items-start">
                <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-4">
                  Select Shift for {selectedServerName}
                </h3>
                <button onClick={closeShiftModal} className="text-gray-400 hover:text-gray-500">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                        Date
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Duration
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Interactions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {selectedServerShifts.map(shift => {
                      // Calculate shift duration
                      let duration = 0;
                      if (shift.shift_created && shift.shift_ended) {
                        const startTime = new Date(shift.shift_created.endsWith('Z')
                          ? shift.shift_created
                          : `${shift.shift_created}Z`);

                        const endTime = new Date(shift.shift_ended.endsWith('Z')
                          ? shift.shift_ended
                          : `${shift.shift_ended}Z`);

                        // Calculate difference and convert to minutes
                        const diffMs = endTime.getTime() - startTime.getTime();
                        duration = Math.round(diffMs / 60000);
                      }

                      // Get interaction count for this shift
                      const interactions = interactionsByShift[shift.shift_id] || [];

                      return (
                        <tr
                          key={shift.shift_id}
                          className="hover:bg-blue-50 cursor-pointer"
                          onClick={() => handleShiftSelect(shift.shift_id)}
                        >
                          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6">
                            <div className="font-medium text-gray-900">
                              {formatDateWithDay(shift.shift_created)}
                            </div>
                            <div className="text-gray-500">
                              {formatDate(shift.shift_created)} - {formatDate(shift.shift_ended)}
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {formatDuration(duration)}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {interactions.length}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-gray-50 px-4 md:px-6 py-3 flex justify-end">
              <button
                onClick={closeShiftModal}
                className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-2 px-4 rounded transition mr-2"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServerSummaryView;